import { z } from 'zod';

/**
 * Schema of Step 1 (Insurance Data) of the Group Life PME quote.
 *
 * The `registerNumber` field accepts a CNPJ (14 digits) or a CAEPF
 * (14 digits); the discrimination is driven by the `isCaepf` toggle
 * (default `false` = CNPJ). Algorithmic validation and on-blur lookups
 * (Brasil API / Compliance.Api) happen inside the UI component — this
 * schema only enforces the shape.
 */
export const stipulatorSchema = z.object({
  /** Insurance type — `1` New, `3` Renewal, `4` Transfer. */
  insuranceType: z.enum(['1', '3', '4']),

  /** `true` when the document is a CAEPF (default `false` = CNPJ). */
  isCaepf: z.boolean(),

  /** Document (CNPJ or CAEPF) — digits only or masked. */
  registerNumber: z.string().min(1, 'Documento é obrigatório'),

  /**
   * CAEPF: CPF consultado na Receita (o `registerNumber` carrega o CAEPF).
   * Mantido separado para o InsureMO gravar `CPF` = CPF e `NoDocumento` = CAEPF
   * (senão ambos ficam com o CAEPF). Vazio para CNPJ ou CAEPF digitado direto.
   */
  cpf: z.string().optional(),

  /** Legal name (CNPJ) or Responsible Name (CAEPF). */
  responsibleName: z.string().min(1, 'Nome é obrigatório'),

  /** CNAE code (BusinessActivityCode) selected on the dropdown. */
  cnaeCode: z.string().min(1, 'Atividade Principal é obrigatória'),

  email: z.string().email('E-mail inválido').or(z.literal('')),

  /** Phone with area code — digits only or masked. */
  phone: z.string(),

  /**
   * Previous policy's insurer company (only when `insuranceType === '4'`
   * Transfer). Loaded from the InsureMO `InsurerCompany` DataTable.
   * Stored value is the insurer's `I4ProID`.
   */
  insurerCompanyId: z.string(),
});

export type StipulatorFormValues = z.infer<typeof stipulatorSchema>;

export const stipulatorDefaultValues: StipulatorFormValues = {
  insuranceType: '1',
  isCaepf: false,
  registerNumber: '',
  cpf: '',
  responsibleName: '',
  cnaeCode: '',
  email: '',
  phone: '',
  insurerCompanyId: '',
};
